import React, { useState, useEffect, useRef, ChangeEvent, KeyboardEvent, useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import { DataTable, SortOrder } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Paginator } from 'primereact/paginator';
import { Menu } from 'primereact/menu';
import BaselineForm from './BaselinesForm';
import BaselinePushdownForm from './BaselinePushdownForm';
import BaselineCopyDrawer from './BaselineCopyDrawer';
import Drawer from '../../Common/Drawer/Drawer';
import Icon from '../../Common/Icon';
import Notification from '../../../components/Common/Notification';
import LoadingIndicator from '../../Common/LoadingIndicator/LoadingIndicator';
import { DEFAULT_PAGE_SIZE, MOBILE_WINDOW_BREAKPOINT } from '../../../constants/constants';
import Routes from '../../../constants/routes';
import { getDataTableSortFields, getSearchParamSortFields } from '../../../helpers/sortHelper';
import { getNullableOnChangeValue } from '../../../helpers/multiselect';
import { useSearchBaselines } from '../../../apis/baselines';
import { useLoadPushdownLocations, usePushdownBaseline, useCopyBaseline } from '../../../apis/baselines';
import { Baseline, BaselinesSearch } from '../../../apis/baselines/baseline.types';
import { MenuItem } from 'primereact/menuitem';
import moment from 'moment';

const ACTION_MENU_OPTIONS = {
    EditProfile: 'editProfile',
    ViewTaskList: 'viewTaskList',
    BaselinePushdown: 'viewBaselinePushdown',
    ViewCopyBaseline: 'viewCopyBaseline',
};

interface BaselinesProps {
    windowWidth: number;
    subscriptionId: number;
    onClose: () => void;
}

const newBaseline = (subscriptionId: number): Baseline => ({
    id: 0,
    name: '',
    description: '',
    isActive: true,
    subscriptionId,
    jobTitles: [],
    users: [],
    userIds: [],
});

const Baselines = ({ windowWidth, subscriptionId, onClose }: BaselinesProps) => {
    const history = useHistory();
    const menuRef = useRef<Menu>(null);

    const emptyBaseline = newBaseline(subscriptionId);

    const [selectedBaseline, setSelectedBaseline] = useState<Baseline | null>(null);
    const [editBaseline, setEditBaseline] = useState<Baseline>(emptyBaseline);
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [pushdownDrawerOpen, setPushdownDrawerOpen] = useState(false);
    const [copyBaselineDrawerOpen, setCopyBaselineDrawerOpen] = useState(false);
    const [isFormValid, setIsFormValid] = useState(true);
    const [isPushdownFormValid, setIsPushdownFormValid] = useState(true);
    const [locationsToPush, setLocationsToPush] = useState<{ id: number }[]>([]);
    const [searchParams, setSearchParams] = useState<BaselinesSearch>({
        subscriptionId,
        isActive: true,
        pageSize: DEFAULT_PAGE_SIZE,
        pageNumber: 1,
        sortBy: 'name',
        sortDir: 'asc',
        name: '',
        description: '',
    });

    // Queries
    const { data: searchResults, isFetching } = useSearchBaselines(searchParams);
    const { data: pushdownData } = useLoadPushdownLocations();

    // Sanitize baselines to ensure all required fields are present and valid for zod
    const sanitizeBaseline = useCallback((b: any): Baseline => ({
        ...b,
        createdDate: b.createdDate && moment(b.createdDate).isValid() ? moment(b.createdDate).toISOString() : new Date().toISOString(),
        createdByUserId: typeof b.createdByUserId === 'number' ? b.createdByUserId : 0,
        subscriptionId: typeof b.subscriptionId === 'number' ? b.subscriptionId : subscriptionId,
    }), [subscriptionId]);

    const baselines = useMemo(() => (searchResults?.results ?? []).map(sanitizeBaseline), [searchResults?.results, sanitizeBaseline]);
    const totalCount = searchResults?.totalCount ?? 0;
    const pushdownLocationList = pushdownData?.locations ?? [];
    const projectEnabled = pushdownData?.isProjectFeatureEnabled ?? false;

    // Mutations
    const { mutate: pushdown, isPending: isPushdownLoading } = usePushdownBaseline((result) => {
        Notification('success', 'Pushdown Successful', 'Baseline pushdown successful');
        setPushdownDrawerOpen(false);
        setLocationsToPush([]);
    });

    const { mutate: copyBaseline, isPending: isCopyLoading } = useCopyBaseline((result) => {
        Notification('success', 'Copy Baseline Successful', '');
        setCopyBaselineDrawerOpen(false);
        setSearchParams((prev) => ({ ...prev, pageNumber: 1 }));
    });

    useEffect(() => {
        document.title = 'Admin';
    }, []);

    const onSearchBlur = () => {
        setSearchParams((prev) => ({
            ...prev,
            name,
            description,
        }));
    };
    const filterNameChange = (e: ChangeEvent<HTMLInputElement>) => {
        setName(e.target.value);
    };

    const keyUpDescription = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' || e.keyCode === 13) {
            setSearchParams((sp) => ({ ...sp, description: description }));
        }
    };

    const filterDescriptionChange = (e: ChangeEvent<HTMLInputElement>) => {
        setDescription(e.target.value);
    };

    const keyUpName = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' || e.keyCode === 13) {
            setSearchParams((sp) => ({ ...sp, name: name }));
        }
    };

    const onActiveChange = (e: DropdownChangeEvent) => {
        const selectedValue = getNullableOnChangeValue(e);
        setSearchParams((prev) => ({ ...prev, isActive: selectedValue }));
    };

    const onPageChange = (e: { page: number }) => {
        setSearchParams((prev) => ({ ...prev, pageNumber: e.page + 1 }));
    };

    const onSort = (event: any) => {
        const { sortField, sortOrder } = event;
        const { sortBy, sortDir } = getSearchParamSortFields(sortField, sortOrder ?? 1);
        if (sortBy !== searchParams.sortBy || sortDir !== searchParams.sortDir) {
            setSearchParams((prev) => ({ ...prev, sortBy, sortDir }));
        }
    };

    // ------- Add New
    const onAddNew = () => {
        setSelectedBaseline(null);
        setEditBaseline(emptyBaseline);
        setDrawerOpen(true);
        setIsFormValid(true);
    };

    // ------- Save / Cancel
    const saveClick = () => {
        setDrawerOpen(false);
        setSearchParams((prev) => ({ ...prev, pageNumber: 1 }));
    };

    const cancelClick = () => {
        setDrawerOpen(false);
        setPushdownDrawerOpen(false);
        setCopyBaselineDrawerOpen(false);
    };

    // ------- Pushdown
    const updateLocationsToPush = (locations: { id: number }[]) => {
        setLocationsToPush(locations);
        setIsPushdownFormValid(true);
    };

    const pushdownClick = () => {
        if (locationsToPush.length < 1) {
            setIsPushdownFormValid(false);
        } else {
            pushdown({
                locationIds: locationsToPush.map((x) => x.id),
                baselineId: editBaseline.id,
            });
        }
    };

    // ------- Copy
    const handleCopyBaseline = (name: string, description: string) => {
        copyBaseline({
            name,
            description,
            baselineId: editBaseline.id,
        });
    };

    // ------- Row Selection & Actions
    const selectBaseline = (e: { value: Baseline; originalEvent: React.MouseEvent }) => {
        const target = e.originalEvent.target as HTMLElement;
        const isEllipsisClick = target.className.includes('ellipsis-h') || target.className.includes('ellipsis');
        if (isEllipsisClick) return;

        setSelectedBaseline(e.value);
        setEditBaseline(e.value);
        history.push(`${Routes.BaselineTaskList}/?bid=${e.value.id}`);
    };

    const onActionsClick = (rowData: Baseline, e: React.MouseEvent) => {
        menuRef.current?.toggle(e);
        setSelectedBaseline(rowData);
        setEditBaseline(rowData);
    };

    const viewBaselineTasks = () => {
        history.push(`${Routes.BaselineTaskList}/?bid=${selectedBaseline?.id}`);
    };

    const viewPushdownDrawer = () => {
        if (pushdownLocationList.length > 0) {
            setDrawerOpen(false);
            setCopyBaselineDrawerOpen(false);
            setPushdownDrawerOpen(true);
            setIsPushdownFormValid(true);
            setLocationsToPush([]);
        } else {
            Notification('error', 'Location Access', 'You must have access to at least 1 location to perform a pushdown');
        }
    };

    const viewCopyBaseline = () => {
        setDrawerOpen(false);
        setPushdownDrawerOpen(false);
        setCopyBaselineDrawerOpen(true);
    };

    const menuOptionClick = (e: { item: { value: string } }) => {
        switch (e.item.value) {
            case ACTION_MENU_OPTIONS.EditProfile:
                setDrawerOpen(true);
                setPushdownDrawerOpen(false);
                setCopyBaselineDrawerOpen(false);
                setIsFormValid(true);
                break;
            case ACTION_MENU_OPTIONS.ViewTaskList:
                viewBaselineTasks();
                break;
            case ACTION_MENU_OPTIONS.BaselinePushdown:
                viewPushdownDrawer();
                break;
            case ACTION_MENU_OPTIONS.ViewCopyBaseline:
                viewCopyBaseline();
                break;
        }
    };

    // ------- Templates
    const activeTemplate = (rowData: Baseline) =>
        rowData.isActive ? (
            <div className='text-success text-center'>
                <Icon name='check-circle' />
            </div>
        ) : (
            <div className='text-danger text-center'>
                <Icon name='times-circle' />
            </div>
        );

    const actionTemplate = (rowData: Baseline) => (
        <div className='text-center ellipsis' onClick={(e) => onActionsClick(rowData, e)}>
            <Icon name='ellipsis-h' />
        </div>
    );

    const nameFilterElement = () => (
        <input
            type='text'
            name='name'
            className='p-inputtext'
            placeholder='Filter by Name'
            onBlur={onSearchBlur}
            onChange={filterNameChange}
            onKeyUp={keyUpName}
            value={name}
        />
    );

    // ------- Render
    const displayColumns = windowWidth < MOBILE_WINDOW_BREAKPOINT ? 'none' : '';

    const activeOptions = [
        { label: 'Yes', value: true },
        { label: 'No', value: false },
        { label: 'All', value: null },
    ];

    const menuOptions: MenuItem[] = [
        { label: 'Edit Baseline', command: (e) => menuOptionClick({ item: { value: ACTION_MENU_OPTIONS.EditProfile } }) },
        { label: 'View Task List', command: (e) => menuOptionClick({ item: { value: ACTION_MENU_OPTIONS.ViewTaskList } }) },
        { label: 'Copy Baseline', command: (e) => menuOptionClick({ item: { value: ACTION_MENU_OPTIONS.ViewCopyBaseline } }) },
    ];

    const activeFilter = (
        <Dropdown
            className='ui-column-filter'
            value={searchParams.isActive}
            options={activeOptions}
            optionLabel='label'
            optionValue='value'
            onChange={onActiveChange}
            placeholder='Select an option'
        />
    );

    const descriptionFilterElement = (
        <input
            type='text'
            name='description'
            className='p-inputtext'
            placeholder='Filter by Description'
            onBlur={onSearchBlur}
            onChange={filterDescriptionChange}
            onKeyUp={keyUpDescription}
            value={description}
        />
    );

    const { sortField, sortOrder } = getDataTableSortFields(searchParams);

    return (
        <div>
            <Menu model={menuOptions} popup ref={menuRef} />

            {drawerOpen && (
                <BaselineForm
                    cancelClick={cancelClick}
                    saveClick={saveClick}
                    baselineId={editBaseline.id}
                    subscriptionId={subscriptionId.toString()}
                />
            )}

            {pushdownDrawerOpen && (
                <Drawer isWide={false} title={editBaseline.name} saveText='Push' onSaveClick={pushdownClick} onCancelClick={cancelClick}>
                    <BaselinePushdownForm
                        pushdownLocations={pushdownLocationList}
                        updateLocationsToPush={updateLocationsToPush}
                        isFormValid={isPushdownFormValid}
                        projectEnabled={projectEnabled}
                        isLoading={isPushdownLoading}
                    />
                </Drawer>
            )}

            {copyBaselineDrawerOpen && (
                <BaselineCopyDrawer
                    originBaselineName={editBaseline.name}
                    isLoading={isCopyLoading}
                    copyBaseline={handleCopyBaseline}
                    cancelClick={cancelClick}
                />
            )}

            <LoadingIndicator isLoading={isFetching} hideWhileLoading>
                <div className='container-fluid'>
                    <div className='row align-items-center justify-content-between mt-2 mb-2'>
                        <div className='col-6'>
                            <h4>
                                <strong>Baselines</strong>
                            </h4>
                        </div>
                        <div className='col-6 d-lg-none'>
                            <button className='btn-close float-end' type='button' onClick={onClose} aria-label='Close' />
                        </div>
                        <div className='col-lg-6 d-flex justify-content-end gap-3 pe-0'>
                            <a
                                className='d-flex align-items-center icon-link icon-link-hover text-decoration-none'
                                href={Routes.CrossBaselineTaskLinkSearch}>
                                <Icon name='link' />
                                Link Search
                            </a>
                            <a className='d-flex align-items-center icon-link icon-link-hover text-decoration-none' href={Routes.CrossBaselineSearch}>
                                <Icon name='search' />
                                Task Search
                            </a>
                            <button type='button' className='btn btn-primary' onClick={onAddNew}>
                                <span className='me-2'>Add New</span>
                                <Icon name='plus-circle' />
                            </button>
                        </div>
                    </div>

                    <div className='row identifier-results-table'>
                        <DataTable
                            filterDisplay='row'
                            className='p-datatable-sm p-datatable-striped'
                            value={baselines}
                            selectionMode='single'
                            selection={selectedBaseline}
                            onSelectionChange={selectBaseline}
                            sortField={sortField}
                            sortOrder={sortOrder as SortOrder}
                            onSort={onSort}>
                            <Column field='name' header='Name' filter filterElement={nameFilterElement()} sortable />
                            <Column
                                field='description'
                                header='Description'
                                filter
                                filterElement={descriptionFilterElement}
                                style={{ display: displayColumns }}
                                sortable
                            />
                            <Column
                                field='isActive'
                                header='Active'
                                body={activeTemplate}
                                style={{ width: '120px' }}
                                filter
                                filterElement={activeFilter}
                                sortable
                            />
                            <Column field='dotdotdot' header='Actions' body={actionTemplate} style={{ width: '6em' }} />
                        </DataTable>

                        {totalCount > searchParams.pageSize && (
                            <Paginator
                                rows={searchParams.pageSize}
                                totalRecords={totalCount}
                                first={(searchParams.pageNumber - 1) * searchParams.pageSize}
                                onPageChange={onPageChange}
                            />
                        )}
                        <div className='results-count'>Total records: {totalCount}</div>
                    </div>
                </div>
            </LoadingIndicator>
        </div>
    );
};

export default Baselines;
